import React, { useState, useEffect } from 'react';
import { Header } from './components/layout/Header';
import { TabNav } from './components/layout/TabNav';
import { Sidebar } from './components/layout/Sidebar';
import { AgendaTab } from './components/tabs/AgendaTab';
import { SocialTab } from './components/tabs/SocialTab';
import { DiscoveryTab } from './components/tabs/DiscoveryTab';
import { ValidationTab } from './components/tabs/ValidationTab';
import { SourcesTab } from './components/tabs/SourcesTab';
import { SoundSystemsTab } from './components/tabs/SoundSystemsTab';
import { ArtistsTab } from './components/tabs/ArtistsTab';
import { MapTab } from './components/tabs/MapTab';
import { CityRadarTab } from './components/tabs/CityRadarTab';
import { SupportTab } from './components/tabs/SupportTab';
import { FestivalsTab } from './components/tabs/FestivalsTab';
import { LegalTab } from './components/tabs/LegalTab';
import { Footer } from './components/common/Footer';
import { api } from './services/api';
import { useAuth } from './contexts/AuthContext';
import type { TabId, Stats } from './types';

export function App() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('agenda');
  const [stats, setStats] = useState<Stats | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [mapCity, setMapCity] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 60000);
    return () => clearInterval(interval);
  }, []);

  async function loadStats() {
    try {
      const data = await api.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }

  async function handleScanNow() {
    setIsScanning(true);
    try {
      await api.runAllScrapers();
      await loadStats();
    } catch (err) {
      console.error('Scan failed:', err);
    } finally {
      setIsScanning(false);
    }
  }

  function handleCityClick(city: string | null) {
    setMapCity(city);
    setActiveTab('map');
  }

  function renderTab() {
    switch (activeTab) {
      case 'agenda': return <AgendaTab stats={stats} onScan={handleScanNow} isScanning={isScanning} onGoToRadar={() => setActiveTab('radar')} />;
      case 'social': return <SocialTab />;
      case 'discovery': return <DiscoveryTab />;
      case 'validation': return <ValidationTab onUpdate={loadStats} />;
      case 'sources': return <SourcesTab />;
      case 'soundsystems': return <SoundSystemsTab />;
      case 'artists': return <ArtistsTab />;
      case 'radar': return <CityRadarTab />;
      case 'map': return <MapTab focusCity={mapCity} onClearCity={() => setMapCity(null)} />;
      case 'support': return <SupportTab onTabChange={setActiveTab} />;
      case 'festivals': return <FestivalsTab />;
      case 'legal': return <LegalTab page="legal" />;
      case 'privacy': return <LegalTab page="privacy" />;
      default: return <AgendaTab stats={stats} onScan={handleScanNow} isScanning={isScanning} onGoToRadar={() => setActiveTab('radar')} />;
    }
  }

  return (
    <div className="app-layout">
      <Header stats={stats} activeTab={activeTab} onTabChange={setActiveTab} />
      <TabNav activeTab={activeTab} onTabChange={(tab) => { setActiveTab(tab); if (tab !== 'map') setMapCity(null); }} pendingCount={stats?.pending_events || 0} />
      <div className="app-body">
        <div className="main-content">
          {renderTab()}
        </div>
        <Sidebar stats={stats} onTabChange={setActiveTab} onCityClick={handleCityClick} />
      </div>
      <Footer onTabChange={(tab) => { setActiveTab(tab); if (tab !== 'map') setMapCity(null); }} />
    </div>
  );
}
